// PageToc.js — the "on this page" sidebar used by long Layered Read panels (Patterns, unit readings).
//
// Modelled on the personality pages' sidebar TOC (views/info/personality_analysis.blade.php .toc):
// small uppercase title, a list of links with a coloured dot, the current section in bold with a
// coloured left bar. Owner, 2026-10-14: "I'd still want a map of what's on the page and the ability to
// go to it — if I've expanded a bunch of stuff it gets unruly … helps people understand what's on the
// page."
//
// Two layouts from one component:
//   • `side`  — a sticky column beside the content (desktop, when there is room);
//   • `strip` — a sticky row of chips above the content (narrow panels and phones).
// The caller owns layout; this file owns the links, the active state and the scrolling.

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

const NAVY = '#14536e';

// Nearest ancestor that actually scrolls. The Layered Read panes scroll inside their own containers
// (not the window), so a window scroll listener would never fire.
export function scrollParentOf(el) {
  let p = el ? el.parentElement : null;
  while (p && p !== document.body) {
    const oy = window.getComputedStyle(p).overflowY;
    // No "is it overflowing yet" test: at mount the content may not be laid out, and a container that
    // cannot scroll yet is still the one that will.
    if (oy === 'auto' || oy === 'scroll') return p;
    p = p.parentElement;
  }
  return null;   // null = the window
}

// Smooth-scroll a section into view inside whatever container holds it. Sections carry
// scroll-margin-top so a sticky strip never covers their heading.
export function scrollToSection(id) {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (!el) return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Smooth only when someone can see it: a background tab never runs the animation, and the jump would
  // silently not happen.
  const smooth = !reduce && document.visibilityState === 'visible';
  el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
}

// Which section is "current": the last one whose top has passed the top of the scroller (plus a
// small allowance). Recomputed on scroll and when the item list changes (cards opening move things).
export function useScrollSpy(ids, anchorRef) {
  const [active, setActive] = useState(ids[0] || null);
  const key = ids.join('|');
  const compute = useCallback(() => {
    if (!ids.length) return;
    const first = document.getElementById(ids[0]);
    const sc = scrollParentOf(anchorRef?.current || first);
    const top = sc ? sc.getBoundingClientRect().top : 0;
    let cur = ids[0];
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el && el.getBoundingClientRect().top - top <= 96) cur = id;
    }
    // At the very bottom the last short section can never reach the top — count it as current.
    if (sc && sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 4) cur = ids[ids.length - 1];
    setActive(prev => (prev === cur ? prev : cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    const first = document.getElementById(ids[0]);
    const sc = scrollParentOf(anchorRef?.current || first);
    const target = sc || window;
    compute();
    target.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => { target.removeEventListener('scroll', compute); window.removeEventListener('resize', compute); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, compute]);
  return [active, compute];
}

export default function PageToc({ items, activeId, layout = 'side', title = 'On this page', onPick = null, stickyTop = 12 }) {
  const go = (it) => { if (onPick) onPick(it); scrollToSection(it.id); };
  // The sidebar scrolls ON ITS OWN when it is taller than the panel it sits in (owner, 2026-10-14: "it
  // doesn't scroll on its own"). The panel scrolls inside its own container, so 100vh was the wrong
  // ceiling — measure the container instead.
  const navRef = useRef(null);
  const [maxH, setMaxH] = useState(null);
  useLayoutEffect(() => {
    if (layout !== 'side' || !navRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const sc = scrollParentOf(navRef.current);
    const measure = () => setMaxH(Math.max(160, (sc ? sc.clientHeight : window.innerHeight) - stickyTop - 24));
    measure();
    const ro = new ResizeObserver(measure);
    if (sc) ro.observe(sc);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [layout, stickyTop]);
  if (!items || items.length < 2) return null;
  const activeItem = items.find(it => it.id === activeId) || null;
  const openParent = activeItem ? (activeItem.indent ? activeItem.parent : activeItem.id) : null;
  if (layout === 'strip') {
    // Narrow panels: the top-level sections as chips, and EVERY destination (including each pattern) in a
    // "Jump to…" menu — the personality pages' mobile .toc-jump-select (Codex G_narrow_toc_patterns).
    const current = items.find(it => it.id === activeId);
    return (
      <nav aria-label={title} style={{ position: 'sticky', top: 0, zIndex: 4, background: 'rgba(255,255,255,.96)',
        borderBottom: '1px solid #e2e8f0', padding: '.45rem 0', marginBottom: '.8rem' }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
          {items.filter(it => !it.indent).map(it => {
            const on = it.id === activeId || (current && current.parent === it.id);
            return (
              <button key={it.id} type="button" onClick={() => go(it)} aria-current={on ? 'true' : undefined}
                style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap',
                  fontSize: '.82rem', fontWeight: on ? 700 : 600, borderRadius: 999, padding: '.2rem .7rem',
                  border: `1.5px solid ${on ? (it.color || NAVY) : '#cbd5e1'}`, background: on ? '#eef4f8' : '#fff', color: '#0f172a' }}>
                {it.color && <span style={{ width: 8, height: 8, borderRadius: 999, background: it.color, flexShrink: 0 }} />}
                {it.label}
              </button>
            );
          })}
        </div>
        {items.some(it => it.indent) && (
          <select aria-label="Jump to a section" value={activeId || ''}
            onChange={(e) => { const it = items.find(x => x.id === e.target.value); if (it) go(it); }}
            style={{ marginTop: 6, width: '100%', fontSize: '.84rem', padding: '.3rem .5rem', borderRadius: 7, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a' }}>
            {items.map(it => (
              <option key={it.id} value={it.id}>{it.indent ? `\u00a0\u00a0\u00a0— ${it.full || it.label}` : (typeof it.label === 'string' ? it.label : it.id)}</option>
            ))}
          </select>
        )}
      </nav>
    );
  }
  return (
    <nav ref={navRef} aria-label={title} style={{ position: 'sticky', top: stickyTop, alignSelf: 'flex-start', maxHeight: maxH ? `${maxH}px` : `calc(100vh - ${stickyTop + 24}px)`, overflowY: 'auto' }}>
      <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b', margin: '0 0 .5rem .2rem' }}>{title}</div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {/* ORIENTATION FIRST (owner, 2026-10-14: everything listed "defeats the goal of orientation"): the
            top-level sections always; the sub-items (each pattern, Protect, …) only for the section you
            are in. The full list is still one click away in each section, and in the narrow jump menu. */}
        {items.filter(it => !it.indent || it.parent === openParent).map(it => {
          const on = it.id === activeId;
          const onPath = !on && !it.indent && it.id === openParent && activeId !== it.id;
          const accent = it.color || NAVY;
          return (
            <li key={it.id}>
              <a href={`#${it.id}`} onClick={(e) => { e.preventDefault(); go(it); }} aria-current={on ? 'true' : undefined}
                title={it.full || it.label}
                style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', borderRadius: 6,
                  padding: it.indent ? '4px 8px 4px 26px' : '6px 8px', margin: '1px 0',
                  fontSize: it.indent ? '.78rem' : '.84rem', fontWeight: (on || onPath) ? 700 : (it.indent ? 500 : 600),
                  color: (on || onPath) ? '#0f172a' : (it.indent ? '#5b6b78' : '#334155'), lineHeight: 1.3,
                  borderLeft: `3px solid ${on ? accent : 'transparent'}`, background: on ? '#eef4f8' : 'transparent' }}>
                {!it.indent && it.color && <span style={{ width: 8, height: 8, borderRadius: 999, background: it.color, flexShrink: 0 }} />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{it.label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
PageToc.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.node, full: PropTypes.string, color: PropTypes.string, indent: PropTypes.bool, parent: PropTypes.string })),
  activeId: PropTypes.string,
  layout: PropTypes.oneOf(['side', 'strip']),
  title: PropTypes.string,
  onPick: PropTypes.func,
  stickyTop: PropTypes.number,
};
